/**
 * Exercise selection for the one-time plan.
 *
 * The catalogue reuses the seed library's slugs and names verbatim
 * (`infrastructure/supabase/seed/seed.sql` §1) so the app's exercise library and the
 * generated programme point at the same rows, then adds the indoor modalities a
 * gym-based product needs: ergs, sleds, carries, boxes, medicine balls, ropes,
 * turfs/ladders, machines, cables and mobility/stability work.
 *
 * Gym-based and indoor only. There are no sprints, no distance runs, no hill work,
 * no field or track drills and no outdoor vocabulary anywhere in this file — and
 * `test/plan-builder.test.ts` guards that with a banned-terms list.
 *
 * Injury handling is additive, never a rewrite of the engine:
 *   1. the engine's `injuryConflictFor` (safety-rails.ts) decides the original map;
 *   2. `PLAN_INJURY_ADDITIONS` closes the gaps the seed's "movement, variant" naming
 *      creates (e.g. "Bench Press, Incline, Dumbbell" does not match the map's
 *      `incline .*press`) and covers the new indoor modalities (jumps, sleds, swings).
 * A blocked movement is never loaded — it is dropped and a clear substitute is
 * selected from the same muscle group.
 */
import type { Equipment, Experience, TrainingTrack } from './intake.js';
import { injuryConflictFor, normaliseInjuryFlag } from '../evaluation/safety-rails.js';
import type { InjuryHit } from '../evaluation/safety-rails.js';
import type { MuscleGroup } from './volume-landmarks.js';

export type SetMeasure = 'reps' | 'distance_m' | 'time_s' | 'amrap';

/** Movement families used for compound-first ordering and pattern rotation. */
export type MovementPattern =
  | 'squat'
  | 'hinge'
  | 'lunge'
  | 'horizontal_push'
  | 'vertical_push'
  | 'horizontal_pull'
  | 'vertical_pull'
  | 'curl'
  | 'triceps_extension'
  | 'lateral_raise'
  | 'calf'
  | 'core'
  | 'carry'
  | 'sled'
  | 'erg'
  | 'plyometric'
  | 'throw'
  | 'rope'
  | 'mobility'
  | 'stability'
  | 'machine_cardio'
  | 'jump_rope';

export type DayStyle = 'resistance' | 'conditioning' | 'power' | 'mobility' | 'mixed';

/**
 * Gym implements (sleds, boxes, medicine balls, ropes, turf/ladders) are covered by
 * the `machine` equipment token: every commercial gym has them, and a customer who
 * selects only `machine` can use the gym floor. A bodyweight-only customer gets
 * bodyweight circuits instead. This keeps the contract's equipment enum unchanged.
 */
export const GYM_IMPLEMENT_EQUIPMENT: Equipment = 'machine';

export interface CatalogueExercise {
  slug: string;
  /** `name` before the first comma, exactly as the seed splits it. */
  movement: string;
  /** `name` after the first comma, or null. */
  variant: string | null;
  name: string;
  muscleGroup: MuscleGroup;
  equipment: Equipment;
  pattern: MovementPattern;
  isCompound: boolean;
  isUnilateral: boolean;
  tracks: ReadonlyArray<TrainingTrack>;
  styles: ReadonlyArray<DayStyle>;
  defaultMeasure: SetMeasure;
  loadIncrementKg: number;
  /** One short coaching cue, carried into the prescription notes. */
  cue: string;
  /** True when there is no external load to estimate. */
  isBodyweight: boolean;
}

interface EntrySpec {
  slug: string;
  name: string;
  muscleGroup: MuscleGroup;
  equipment: Equipment;
  pattern: MovementPattern;
  tracks: ReadonlyArray<TrainingTrack>;
  compound?: boolean;
  unilateral?: boolean;
  styles?: ReadonlyArray<DayStyle>;
  measure?: SetMeasure;
  increment?: number;
  cue: string;
  bodyweight?: boolean;
}

/** `"Bench Press, Barbell"` -> movement `"Bench Press"`, variant `"Barbell"`. */
export function splitExerciseName(name: string): { movement: string; variant: string | null } {
  const comma = name.indexOf(',');
  if (comma === -1) return { movement: name.trim(), variant: null };
  return {
    movement: name.slice(0, comma).trim(),
    variant: name.slice(comma + 1).trim() || null,
  };
}

function ex(spec: EntrySpec): CatalogueExercise {
  const { movement, variant } = splitExerciseName(spec.name);
  const compound = spec.compound ?? false;
  return {
    slug: spec.slug,
    movement,
    variant,
    name: spec.name,
    muscleGroup: spec.muscleGroup,
    equipment: spec.equipment,
    pattern: spec.pattern,
    isCompound: compound,
    isUnilateral: spec.unilateral ?? false,
    tracks: spec.tracks,
    styles: spec.styles ?? ['resistance'],
    defaultMeasure: spec.measure ?? 'reps',
    loadIncrementKg: spec.increment ?? (compound ? 2.5 : 1.25),
    cue: spec.cue,
    isBodyweight: spec.bodyweight ?? spec.equipment === 'bodyweight',
  };
}

const RESISTANCE_TRACKS: ReadonlyArray<TrainingTrack> = ['strength', 'hypertrophy', 'general'];
const ALL_RESISTANCE_AND_PERFORMANCE: ReadonlyArray<TrainingTrack> = [
  'strength',
  'hypertrophy',
  'performance',
  'general',
];

/**
 * The full catalogue. Seed slugs/names first (verbatim), then the indoor additions.
 * Conditional on `source` in the phase report, the additions need seeding into
 * `public.exercises` with these exact slugs.
 */
export const EXERCISE_CATALOGUE: ReadonlyArray<CatalogueExercise> = [
  // --- seed library: lower body -------------------------------------------
  ex({ slug: 'squat-ssb', name: 'Squat, SSB', muscleGroup: 'quads', equipment: 'barbell', pattern: 'squat', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, cue: 'Brace, sit between the hips; knees travel over the toes.' }),
  ex({ slug: 'split-squat-bulgarian-rfe', name: 'Split Squat, Bulgarian/RFE', muscleGroup: 'quads', equipment: 'barbell', pattern: 'lunge', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, unilateral: true, cue: 'Front shin vertical; drive through the mid-foot.' }),
  ex({ slug: 'squat-single-leg-to-box', name: 'Squat, Single Leg, To Box', muscleGroup: 'quads', equipment: 'bodyweight', pattern: 'squat', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, unilateral: true, cue: 'Sit back to the box; knee travels over the toe.' }),
  ex({ slug: 'leg-extensions', name: 'Leg Extensions', muscleGroup: 'quads', equipment: 'machine', pattern: 'squat', tracks: RESISTANCE_TRACKS, cue: 'Squeeze at the top; control the return.' }),
  ex({ slug: 'leg-press', name: 'Leg Press', muscleGroup: 'quads', equipment: 'machine', pattern: 'squat', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Feet flat; do not let the hips roll off the pad.' }),
  ex({ slug: 'hack-squat', name: 'Hack Squat', muscleGroup: 'quads', equipment: 'machine', pattern: 'squat', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Back flat on the pad; sink under control.' }),
  ex({ slug: 'front-squat-barbell', name: 'Front Squat, Barbell', muscleGroup: 'quads', equipment: 'barbell', pattern: 'squat', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, cue: 'Elbows high; stay upright out of the bottom.' }),
  ex({ slug: 'goblet-squat', name: 'Goblet Squat', muscleGroup: 'quads', equipment: 'kettlebell', pattern: 'squat', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Elbows inside the knees; chest tall.' }),
  ex({ slug: 'rdl-single-leg', name: 'RDL, Single Leg', muscleGroup: 'hamstrings', equipment: 'dumbbell', pattern: 'hinge', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, unilateral: true, cue: 'Hinge, do not squat; hips level throughout.' }),
  ex({ slug: 'deadlift-trap-bar', name: 'Deadlift, Trap Bar', muscleGroup: 'hamstrings', equipment: 'barbell', pattern: 'hinge', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, cue: 'Hips high, chest up; push the floor away.' }),
  ex({ slug: 'rdl-barbell', name: 'RDL, Barbell', muscleGroup: 'hamstrings', equipment: 'barbell', pattern: 'hinge', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, cue: 'Hinge, do not squat; bar close to the legs.' }),
  ex({ slug: 'leg-curl', name: 'Leg Curl', muscleGroup: 'hamstrings', equipment: 'machine', pattern: 'hinge', tracks: RESISTANCE_TRACKS, cue: 'Curl with the hamstring, not the lower back.' }),
  ex({ slug: 'back-extension', name: 'Back Extension', muscleGroup: 'hamstrings', equipment: 'machine', pattern: 'hinge', tracks: RESISTANCE_TRACKS, cue: 'Move from the hips; stop at a neutral spine.' }),
  ex({ slug: 'kettlebell-swing', name: 'Kettlebell Swing', muscleGroup: 'hamstrings', equipment: 'kettlebell', pattern: 'hinge', tracks: [...ALL_RESISTANCE_AND_PERFORMANCE, 'conditioning'], compound: true, styles: ['resistance', 'conditioning'], cue: 'Snap the hips; the arms are ropes.' }),
  ex({ slug: 'kettlebell-deadlift', name: 'Kettlebell Deadlift', muscleGroup: 'hamstrings', equipment: 'kettlebell', pattern: 'hinge', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Brace, then stand tall; do not yank.' }),
  ex({ slug: 'hip-airplane', name: 'Hip Airplane', muscleGroup: 'glutes', equipment: 'bodyweight', pattern: 'stability', tracks: [...RESISTANCE_TRACKS, 'wellness'], unilateral: true, styles: ['resistance', 'mobility'], cue: 'Hips square; slow controlled rotation.' }),
  ex({ slug: 'hip-thrust-barbell', name: 'Hip Thrust, Barbell', muscleGroup: 'glutes', equipment: 'barbell', pattern: 'hinge', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, cue: 'Chin tucked; finish with the glutes, not the back.' }),
  ex({ slug: 'glute-bridge', name: 'Glute Bridge', muscleGroup: 'glutes', equipment: 'bodyweight', pattern: 'hinge', tracks: [...RESISTANCE_TRACKS, 'wellness'], styles: ['resistance', 'mobility'], cue: 'Ribs down; squeeze at the top.' }),

  // --- seed library: upper body -------------------------------------------
  ex({ slug: 'bench-press-barbell', name: 'Bench Press, Barbell', muscleGroup: 'chest', equipment: 'barbell', pattern: 'horizontal_push', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, cue: 'Shoulder blades pinned; bar to the lower sternum.' }),
  ex({ slug: 'bench-press-incline-dumbbell', name: 'Bench Press, Incline, Dumbbell', muscleGroup: 'chest', equipment: 'dumbbell', pattern: 'horizontal_push', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Bench at 30 degrees; elbows under the wrists.' }),
  ex({ slug: 'lat-pulldown-neutral-grip', name: 'Lat Pulldown, Neutral Grip', muscleGroup: 'back', equipment: 'cable', pattern: 'vertical_pull', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Chest up; elbows down and back.' }),
  ex({ slug: 'lat-pulldown-single-arm', name: 'Lat Pulldown, Single Arm', muscleGroup: 'back', equipment: 'cable', pattern: 'vertical_pull', tracks: RESISTANCE_TRACKS, compound: true, unilateral: true, cue: 'Reach long at the top; pull the elbow to the hip.' }),
  ex({ slug: 'row-single-arm-knee-supported-db', name: 'Row, Single Arm, Knee Supported, DB', muscleGroup: 'back', equipment: 'dumbbell', pattern: 'horizontal_pull', tracks: RESISTANCE_TRACKS, compound: true, unilateral: true, cue: 'Flat back; elbow to the hip.' }),
  ex({ slug: 'row-chest-supported-barbell', name: 'Row, Chest Supported, Barbell', muscleGroup: 'back', equipment: 'barbell', pattern: 'horizontal_pull', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Chest glued to the pad; bar to the navel.' }),
  ex({ slug: 'shoulder-press-single-arm-half-kneeling-db', name: 'Shoulder Press, Single Arm, Half Kneeling, DB', muscleGroup: 'shoulders', equipment: 'dumbbell', pattern: 'vertical_push', tracks: RESISTANCE_TRACKS, compound: true, unilateral: true, cue: 'Ribs down, glutes tight; press past the ear.' }),
  ex({ slug: 'viking-press', name: 'Viking Press', muscleGroup: 'shoulders', equipment: 'machine', pattern: 'vertical_push', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Ribs down; press through the handles.' }),
  ex({ slug: 'face-pulls-cable', name: 'Face Pulls, Cable', muscleGroup: 'shoulders', equipment: 'cable', pattern: 'lateral_raise', tracks: RESISTANCE_TRACKS, cue: 'Rope to the forehead; externally rotate at the end.' }),
  ex({ slug: 'triceps-pushdowns-cables', name: 'Triceps Pushdowns, Cables', muscleGroup: 'triceps', equipment: 'cable', pattern: 'triceps_extension', tracks: RESISTANCE_TRACKS, cue: 'Elbows fixed; full lockout.' }),
  ex({ slug: 'seated-bicep-curls-db', name: 'Seated Bicep Curls, DB', muscleGroup: 'biceps', equipment: 'dumbbell', pattern: 'curl', tracks: RESISTANCE_TRACKS, cue: 'Elbows pinned; no swinging.' }),
  ex({ slug: 'heel-raise-bilateral', name: 'Heel Raise, Bilateral', muscleGroup: 'calves', equipment: 'barbell', pattern: 'calf', tracks: RESISTANCE_TRACKS, cue: 'Full stretch at the bottom; pause at the top.' }),
  ex({ slug: 'rollout-ab-wheel', name: 'Rollout, Ab Wheel', muscleGroup: 'core', equipment: 'bodyweight', pattern: 'core', tracks: RESISTANCE_TRACKS, cue: 'Ribs down, glutes on; only as far as you can brace.' }),

  // --- seed library: indoor cardio / sled ---------------------------------
  ex({ slug: 'ski-erg', name: 'Ski Erg', muscleGroup: 'cardio', equipment: 'machine', pattern: 'erg', tracks: ['conditioning', 'performance', 'general'], styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Hips back, long spine; drive the handles past the hips.' }),
  ex({ slug: 'sled-pull', name: 'Sled Pull', muscleGroup: 'full_body', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'sled', tracks: ['conditioning', 'performance', 'general'], compound: true, styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Stay low; short powerful steps.' }),
  ex({ slug: 'sled-push', name: 'Sled Push', muscleGroup: 'full_body', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'sled', tracks: ['conditioning', 'performance', 'general'], compound: true, styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Arms locked out; drive through the balls of the feet.' }),

  // --- indoor additions: lower body ---------------------------------------
  ex({ slug: 'machine-chest-press', name: 'Chest Press, Machine', muscleGroup: 'chest', equipment: 'machine', pattern: 'horizontal_push', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Handles at chest height; press without shrugging.' }),
  ex({ slug: 'cable-fly', name: 'Cable Fly', muscleGroup: 'chest', equipment: 'cable', pattern: 'horizontal_push', tracks: RESISTANCE_TRACKS, cue: 'Soft elbows; bring the hands together, not past.' }),
  ex({ slug: 'push-up', name: 'Push-Up', muscleGroup: 'chest', equipment: 'bodyweight', pattern: 'horizontal_push', tracks: [...ALL_RESISTANCE_AND_PERFORMANCE, 'wellness'], compound: true, styles: ['resistance', 'mobility'], cue: 'Body in one line; elbows at 45 degrees.' }),
  ex({ slug: 'dip', name: 'Dip', muscleGroup: 'chest', equipment: 'bodyweight', pattern: 'horizontal_push', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, cue: 'Lean slightly forward; stop at a pain-free depth.' }),
  ex({ slug: 'push-up-band', name: 'Push-Up, Band', muscleGroup: 'chest', equipment: 'band', pattern: 'horizontal_push', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Band across the upper back; press to full lockout.' }),

  ex({ slug: 'seated-row-cable', name: 'Seated Row, Cable', muscleGroup: 'back', equipment: 'cable', pattern: 'horizontal_pull', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Tall chest; drive the elbows back and down.' }),
  ex({ slug: 'pull-up', name: 'Pull-Up', muscleGroup: 'back', equipment: 'bodyweight', pattern: 'vertical_pull', tracks: [...ALL_RESISTANCE_AND_PERFORMANCE, 'wellness'], compound: true, styles: ['resistance', 'mobility'], cue: 'Start from a dead hang; chin over the bar.' }),
  ex({ slug: 'chin-up', name: 'Chin-Up', muscleGroup: 'back', equipment: 'bodyweight', pattern: 'vertical_pull', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Elbows down; no swinging.' }),
  ex({ slug: 'inverted-row', name: 'Inverted Row', muscleGroup: 'back', equipment: 'bodyweight', pattern: 'horizontal_pull', tracks: [...ALL_RESISTANCE_AND_PERFORMANCE, 'wellness'], compound: true, styles: ['resistance', 'mobility'], cue: 'Straight line from heel to head; chest to the bar.' }),
  ex({ slug: 'suspension-row', name: 'Suspension Row', muscleGroup: 'back', equipment: 'bodyweight', pattern: 'horizontal_pull', tracks: [...ALL_RESISTANCE_AND_PERFORMANCE, 'wellness'], compound: true, styles: ['resistance', 'mobility'], cue: 'Handles over the mid-chest; ribs down.' }),
  ex({ slug: 'band-row', name: 'Row, Band', muscleGroup: 'back', equipment: 'band', pattern: 'horizontal_pull', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Anchor at chest height; pull the shoulder blade first.' }),

  ex({ slug: 'overhead-press-barbell', name: 'Overhead Press, Barbell', muscleGroup: 'shoulders', equipment: 'barbell', pattern: 'vertical_push', tracks: ALL_RESISTANCE_AND_PERFORMANCE, compound: true, cue: 'Ribs down; bar path over the mid-foot.' }),
  ex({ slug: 'kettlebell-press', name: 'Kettlebell Press', muscleGroup: 'shoulders', equipment: 'kettlebell', pattern: 'vertical_push', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Wrist stacked; press past the ear.' }),
  ex({ slug: 'lateral-raise-db', name: 'Lateral Raise, DB', muscleGroup: 'shoulders', equipment: 'dumbbell', pattern: 'lateral_raise', tracks: RESISTANCE_TRACKS, cue: 'Lead with the elbow; stop at shoulder height.' }),
  ex({ slug: 'cable-lateral-raise', name: 'Lateral Raise, Cable', muscleGroup: 'shoulders', equipment: 'cable', pattern: 'lateral_raise', tracks: RESISTANCE_TRACKS, cue: 'Smooth arc; no shrugging.' }),
  ex({ slug: 'band-pull-apart', name: 'Band Pull-Apart', muscleGroup: 'shoulders', equipment: 'band', pattern: 'lateral_raise', tracks: RESISTANCE_TRACKS, cue: 'Straight arms; squeeze the shoulder blades.' }),
  ex({ slug: 'band-external-rotation', name: 'External Rotation, Band', muscleGroup: 'shoulders', equipment: 'band', pattern: 'stability', tracks: [...RESISTANCE_TRACKS, 'wellness'], styles: ['resistance', 'mobility'], cue: 'Elbow pinned at the side; rotate slowly.' }),
  ex({ slug: 'band-scapular-retraction', name: 'Scapular Retraction, Band', muscleGroup: 'shoulders', equipment: 'band', pattern: 'stability', tracks: [...RESISTANCE_TRACKS, 'wellness'], styles: ['resistance', 'mobility'], cue: 'Pull the shoulder blades together; hold one beat.' }),

  ex({ slug: 'hammer-curl-db', name: 'Hammer Curl, DB', muscleGroup: 'biceps', equipment: 'dumbbell', pattern: 'curl', tracks: RESISTANCE_TRACKS, cue: 'Neutral wrists; elbows still.' }),
  ex({ slug: 'cable-curl', name: 'Cable Curl', muscleGroup: 'biceps', equipment: 'cable', pattern: 'curl', tracks: RESISTANCE_TRACKS, cue: 'Constant tension; no lean.' }),
  ex({ slug: 'close-grip-bench-barbell', name: 'Close-Grip Bench, Barbell', muscleGroup: 'triceps', equipment: 'barbell', pattern: 'horizontal_push', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Elbows tucked; press to lockout.' }),
  ex({ slug: 'skull-crusher-ez-bar', name: 'Skull Crusher, EZ Bar', muscleGroup: 'triceps', equipment: 'barbell', pattern: 'triceps_extension', tracks: RESISTANCE_TRACKS, cue: 'Elbows still; lower behind the forehead.' }),
  ex({ slug: 'overhead-triceps-extension-db', name: 'Overhead Triceps Extension, DB', muscleGroup: 'triceps', equipment: 'dumbbell', pattern: 'triceps_extension', tracks: RESISTANCE_TRACKS, cue: 'Elbows narrow; full stretch.' }),
  ex({ slug: 'calf-raise-seated', name: 'Calf Raise, Seated', muscleGroup: 'calves', equipment: 'machine', pattern: 'calf', tracks: RESISTANCE_TRACKS, cue: 'Pause at the top; slow on the way down.' }),
  ex({ slug: 'hip-thrust-machine', name: 'Hip Thrust, Machine', muscleGroup: 'glutes', equipment: 'machine', pattern: 'hinge', tracks: RESISTANCE_TRACKS, compound: true, cue: 'Pad across the hips; finish tall.' }),
  ex({ slug: 'leg-curl-seated', name: 'Leg Curl, Seated', muscleGroup: 'hamstrings', equipment: 'machine', pattern: 'hinge', tracks: RESISTANCE_TRACKS, cue: 'Curl down and in; control the return.' }),

  // --- indoor conditioning: ergs, carries, circuits -----------------------
  ex({ slug: 'rower-erg', name: 'Row, Erg', muscleGroup: 'cardio', equipment: 'machine', pattern: 'erg', tracks: ['conditioning', 'general', 'performance'], styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Legs, then body, then arms; reverse on the way back.' }),
  ex({ slug: 'bike-erg', name: 'Bike Erg', muscleGroup: 'cardio', equipment: 'machine', pattern: 'machine_cardio', tracks: ['conditioning', 'general', 'wellness'], styles: ['conditioning', 'mobility'], measure: 'distance_m', increment: 0, cue: 'Steady cadence; stay seated and smooth.' }),
  ex({ slug: 'air-bike', name: 'Air Bike', muscleGroup: 'cardio', equipment: 'machine', pattern: 'machine_cardio', tracks: ['conditioning', 'general'], styles: ['conditioning'], measure: 'time_s', increment: 0, cue: 'Hard on the work interval; keep the arms driving.' }),
  ex({ slug: 'prowler-push', name: 'Prowler Push', muscleGroup: 'full_body', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'sled', tracks: ['conditioning', 'performance'], compound: true, styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Low body angle; short aggressive steps.' }),
  ex({ slug: 'farmer-carry', name: 'Carry, Farmer', muscleGroup: 'forearms', equipment: 'dumbbell', pattern: 'carry', tracks: ['conditioning', 'general'], compound: true, styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Tall posture; do not let the ribs flare.' }),
  ex({ slug: 'suitcase-carry', name: 'Carry, Suitcase', muscleGroup: 'core', equipment: 'dumbbell', pattern: 'carry', tracks: ['conditioning', 'general', 'performance'], compound: true, styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Stay square; resist the sideways pull.' }),
  ex({ slug: 'front-rack-carry', name: 'Carry, Front Rack', muscleGroup: 'core', equipment: 'kettlebell', pattern: 'carry', tracks: ['conditioning', 'performance'], compound: true, styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Elbows high; brace and walk tall.' }),
  ex({ slug: 'overhead-carry', name: 'Carry, Overhead', muscleGroup: 'shoulders', equipment: 'kettlebell', pattern: 'carry', tracks: ['conditioning', 'performance'], compound: true, styles: ['conditioning'], measure: 'distance_m', increment: 0, cue: 'Wrist stacked over the shoulder; ribs down.' }),
  ex({ slug: 'battle-rope-waves', name: 'Battle Rope Waves', muscleGroup: 'shoulders', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'rope', tracks: ['conditioning', 'general'], styles: ['conditioning'], measure: 'time_s', increment: 0, cue: 'Athletic stance; keep the waves moving.' }),
  ex({ slug: 'agility-ladder', name: 'Agility Ladder', muscleGroup: 'full_body', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'machine_cardio', tracks: ['conditioning', 'performance'], styles: ['conditioning'], measure: 'time_s', increment: 0, cue: 'Quick, light feet through the ladder; stay tall.' }),
  ex({ slug: 'lateral-step-shuffle', name: 'Lateral Step, Shuffle', muscleGroup: 'glutes', equipment: 'bodyweight', pattern: 'machine_cardio', tracks: ['conditioning', 'wellness', 'performance'], styles: ['conditioning', 'mobility'], measure: 'time_s', increment: 0, cue: 'Stay low in the hips; short controlled steps.' }),
  ex({ slug: 'carioca', name: 'Carioca', muscleGroup: 'full_body', equipment: 'bodyweight', pattern: 'machine_cardio', tracks: ['conditioning', 'performance'], styles: ['conditioning'], measure: 'time_s', increment: 0, cue: 'Rotate from the hips; keep the shoulders facing forward.' }),
  ex({ slug: 'jump-rope', name: 'Jump Rope', muscleGroup: 'calves', equipment: 'bodyweight', pattern: 'jump_rope', tracks: ['conditioning', 'general'], styles: ['conditioning'], measure: 'time_s', increment: 0, cue: 'Small hops; wrists do the work.' }),
  ex({ slug: 'med-ball-slam', name: 'Med Ball Slam', muscleGroup: 'full_body', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'throw', tracks: ['conditioning', 'performance', 'general'], styles: ['conditioning', 'power'], measure: 'reps', increment: 0, cue: 'Full overhead reach, then slam; exhale hard.' }),
  ex({ slug: 'med-ball-rotational-throw', name: 'Med Ball Rotational Throw', muscleGroup: 'core', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'throw', tracks: ['conditioning', 'performance'], styles: ['conditioning', 'power'], measure: 'reps', increment: 0, cue: 'Rotate through the hips; throw into the wall.' }),
  ex({ slug: 'conditioning-circuit-amrap', name: 'Circuit, Bodyweight', muscleGroup: 'full_body', equipment: 'bodyweight', pattern: 'core', tracks: ['conditioning', 'general', 'wellness'], styles: ['conditioning'], measure: 'amrap', increment: 0, cue: 'Move through the circuit with clean reps; rest only when form slips.' }),
  ex({ slug: 'med-ball-chest-throw', name: 'Med Ball Chest Throw', muscleGroup: 'chest', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'throw', tracks: ['performance', 'conditioning'], styles: ['power'], measure: 'reps', increment: 0, cue: 'Explode forward; catch and reset each rep.' }),

  // --- indoor performance: jumps, throws, derivatives ---------------------
  ex({ slug: 'box-jump', name: 'Box Jump', muscleGroup: 'quads', equipment: GYM_IMPLEMENT_EQUIPMENT, pattern: 'plyometric', tracks: ['performance', 'conditioning'], compound: true, styles: ['power'], measure: 'reps', increment: 0, cue: 'Land soft and quiet; step down, do not rebound.' }),
  ex({ slug: 'vertical-jump', name: 'Vertical Jump', muscleGroup: 'quads', equipment: 'bodyweight', pattern: 'plyometric', tracks: ['performance', 'conditioning'], compound: true, styles: ['power'], measure: 'reps', increment: 0, cue: 'Dip and explode; reset every rep.' }),
  ex({ slug: 'trap-bar-jump', name: 'Trap Bar Jump', muscleGroup: 'quads', equipment: 'barbell', pattern: 'plyometric', tracks: ['performance'], compound: true, styles: ['power'], measure: 'reps', increment: 2.5, cue: 'Light load, max intent; land soft.' }),
  ex({ slug: 'hang-clean', name: 'Hang Clean', muscleGroup: 'full_body', equipment: 'barbell', pattern: 'plyometric', tracks: ['performance', 'strength'], compound: true, styles: ['power', 'resistance'], measure: 'reps', increment: 2.5, cue: 'Fast elbows; catch in a quarter squat.' }),
  ex({ slug: 'clean-pull', name: 'Clean Pull', muscleGroup: 'hamstrings', equipment: 'barbell', pattern: 'plyometric', tracks: ['performance'], compound: true, styles: ['power'], measure: 'reps', increment: 2.5, cue: 'Push the floor away; finish tall and shrug.' }),
  ex({ slug: 'push-press', name: 'Push Press', muscleGroup: 'shoulders', equipment: 'barbell', pattern: 'vertical_push', tracks: ['performance', 'strength'], compound: true, styles: ['power', 'resistance'], measure: 'reps', increment: 2.5, cue: 'Short dip, big drive; lock out overhead.' }),

  // --- mobility, stability and recovery -----------------------------------
  ex({ slug: 'cat-cow', name: 'Cat-Cow', muscleGroup: 'back', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'conditioning', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Move with the breath; no forcing.' }),
  ex({ slug: 'ninety-ninety-hip', name: '90/90 Hip Switch', muscleGroup: 'glutes', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Sit tall; switch slowly side to side.' }),
  ex({ slug: 'thoracic-rotation', name: 'Thoracic Rotation', muscleGroup: 'back', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Rotate from the ribcage; hips stay put.' }),
  ex({ slug: 'world-greatest-stretch', name: 'World Greatest Stretch', muscleGroup: 'full_body', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Long lunge, elbow to instep, then open.' }),
  ex({ slug: 'ankle-dorsiflexion-rock', name: 'Ankle Dorsiflexion Rock', muscleGroup: 'calves', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Heel down; knee travels over the toes.' }),
  ex({ slug: 'hamstring-stretch', name: 'Hamstring Stretch', muscleGroup: 'hamstrings', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Hinge from the hips; keep a neutral spine.' }),
  ex({ slug: 'couch-stretch', name: 'Couch Stretch', muscleGroup: 'quads', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Ribs down; squeeze the glute of the back leg.' }),
  ex({ slug: 'foam-roll-quads', name: 'Foam Roll, Quads', muscleGroup: 'quads', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Slow passes; breathe out on the tender spots.' }),
  ex({ slug: 'foam-roll-back', name: 'Foam Roll, Back', muscleGroup: 'back', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Roll the mid-back, not the lower back.' }),
  ex({ slug: 'band-dislocate', name: 'Band Dislocate', muscleGroup: 'shoulders', equipment: 'band', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Wide grip; move only through a comfortable arc.' }),
  ex({ slug: 'wall-slide', name: 'Wall Slide', muscleGroup: 'shoulders', equipment: 'bodyweight', pattern: 'mobility', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Forearms on the wall; slide without shrugging.' }),
  ex({ slug: 'plank', name: 'Plank', muscleGroup: 'core', equipment: 'bodyweight', pattern: 'stability', tracks: ['wellness', 'general', 'performance', ...RESISTANCE_TRACKS], styles: ['mobility', 'resistance'], measure: 'time_s', increment: 0, cue: 'Ribs down, glutes on; one straight line.' }),
  ex({ slug: 'side-plank', name: 'Side Plank', muscleGroup: 'core', equipment: 'bodyweight', pattern: 'stability', tracks: ['wellness', 'general', 'performance', ...RESISTANCE_TRACKS], styles: ['mobility', 'resistance'], measure: 'time_s', increment: 0, cue: 'Stack the hips; push the floor away.' }),
  ex({ slug: 'dead-bug', name: 'Dead Bug', muscleGroup: 'core', equipment: 'bodyweight', pattern: 'stability', tracks: ['wellness', 'general', 'performance', ...RESISTANCE_TRACKS], styles: ['mobility', 'resistance'], cue: 'Low back flat; opposite arm and leg.' }),
  ex({ slug: 'bird-dog', name: 'Bird Dog', muscleGroup: 'core', equipment: 'bodyweight', pattern: 'stability', tracks: ['wellness', 'general', 'performance', ...RESISTANCE_TRACKS], styles: ['mobility', 'resistance'], cue: 'Reach long; do not rotate the hips.' }),
  ex({ slug: 'single-leg-balance', name: 'Single Leg Balance', muscleGroup: 'glutes', equipment: 'bodyweight', pattern: 'stability', tracks: ['wellness', 'general', 'performance'], styles: ['mobility'], measure: 'time_s', increment: 0, cue: 'Tall posture; hold the range you own.' }),
  ex({ slug: 'cable-pallof-press', name: 'Pallof Press, Cable', muscleGroup: 'core', equipment: 'cable', pattern: 'stability', tracks: ['wellness', 'general', 'performance', ...RESISTANCE_TRACKS], styles: ['mobility', 'resistance'], cue: 'Resist the rotation; press straight out.' }),
  ex({ slug: 'band-pallof-press', name: 'Pallof Press, Band', muscleGroup: 'core', equipment: 'band', pattern: 'stability', tracks: ['wellness', 'general', 'performance', ...RESISTANCE_TRACKS], styles: ['mobility', 'resistance'], cue: 'Ribs down; do not let the band twist you.' }),
  ex({ slug: 'leg-press-light', name: 'Leg Press, Light', muscleGroup: 'quads', equipment: 'machine', pattern: 'squat', tracks: ['wellness'], compound: true, styles: ['mobility'], cue: 'Light load, full control, pain-free range only.' }),
  ex({ slug: 'seated-row-light-cable', name: 'Seated Row, Light, Cable', muscleGroup: 'back', equipment: 'cable', pattern: 'horizontal_pull', tracks: ['wellness'], compound: true, styles: ['mobility'], cue: 'Light and smooth; tall chest.' }),
];

/**
 * Extra injury routing the engine's map does not cover, because the seed names the
 * movement first ("Bench Press, Incline, Dumbbell") or because the modality is new
 * (jumps, sleds, swings, carries). Same shape as `INJURY_MOVEMENT_MAP`.
 */
export interface PlanInjuryPattern {
  flag: string;
  family: string;
  pattern: RegExp;
  substitute: string;
}

export const PLAN_INJURY_ADDITIONS: ReadonlyArray<PlanInjuryPattern> = [
  {
    flag: 'left_shoulder',
    family: 'incline-press',
    pattern: /incline/i,
    substitute: 'flat or floor press with a neutral grip',
  },
  {
    flag: 'right_shoulder',
    family: 'incline-press',
    pattern: /incline/i,
    substitute: 'flat or floor press with a neutral grip',
  },
  {
    flag: 'left_shoulder',
    family: 'overhead-press',
    pattern: /kettlebell press|overhead carry|push press|push-up, band/i,
    substitute: 'neutral-grip dumbbell press at a pain-free angle',
  },
  {
    flag: 'right_shoulder',
    family: 'overhead-press',
    pattern: /kettlebell press|overhead carry|push press|push-up, band/i,
    substitute: 'neutral-grip dumbbell press at a pain-free angle',
  },
  {
    flag: 'left_shoulder',
    family: 'overhead-mobility',
    pattern: /dislocate/i,
    substitute: 'wall slide through a comfortable arc',
  },
  {
    flag: 'right_shoulder',
    family: 'overhead-mobility',
    pattern: /dislocate/i,
    substitute: 'wall slide through a comfortable arc',
  },
  {
    flag: 'left_knee',
    family: 'plyometric',
    pattern: /jump|jump rope|plyometric|sled|prowler/i,
    substitute: 'leg press or hip thrust at a pain-free range',
  },
  {
    flag: 'right_knee',
    family: 'plyometric',
    pattern: /jump|jump rope|plyometric|sled|prowler/i,
    substitute: 'leg press or hip thrust at a pain-free range',
  },
  {
    flag: 'lower_back',
    family: 'loaded-hinge',
    pattern: /deadlift|swing|clean|carry/i,
    substitute: 'chest-supported row or seated leg curl',
  },
  {
    flag: 'left_elbow',
    family: 'throw',
    pattern: /throw|slam|battle rope/i,
    substitute: 'band triceps extension at a pain-free angle',
  },
  {
    flag: 'right_elbow',
    family: 'throw',
    pattern: /throw|slam|battle rope/i,
    substitute: 'band triceps extension at a pain-free angle',
  },
  {
    flag: 'left_wrist',
    family: 'carry',
    pattern: /carry|battle rope|push-up, band/i,
    substitute: 'neutral-grip carry with wrist straps, or a machine alternative',
  },
  {
    flag: 'right_wrist',
    family: 'carry',
    pattern: /carry|battle rope|push-up, band/i,
    substitute: 'neutral-grip carry with wrist straps, or a machine alternative',
  },
];

export interface PlanInjuryHit {
  flag: string;
  family: string;
  substitute: string;
  source: 'safety_rail' | 'plan_addition';
}

/** First add-on conflict for a movement name, or `null`. */
export function planInjuryConflictFor(
  exerciseName: string,
  injuryFlags: ReadonlyArray<string>,
): PlanInjuryHit | null {
  const normalised = injuryFlags.map(normaliseInjuryFlag);
  for (const rule of PLAN_INJURY_ADDITIONS) {
    if (!normalised.includes(rule.flag)) continue;
    if (rule.pattern.test(exerciseName)) {
      return {
        flag: rule.flag,
        family: rule.family,
        substitute: rule.substitute,
        source: 'plan_addition',
      };
    }
  }
  return null;
}

/**
 * The combined injury check: the engine's safety rail first, then the plan add-ons.
 * `null` means the movement is clear to load.
 */
export function injuryBlockFor(
  exerciseName: string,
  injuryFlags: ReadonlyArray<string>,
): PlanInjuryHit | null {
  const engineHit: InjuryHit | null = injuryConflictFor(exerciseName, injuryFlags);
  if (engineHit) {
    return {
      flag: engineHit.flag,
      family: engineHit.family,
      substitute: engineHit.substitute,
      source: 'safety_rail',
    };
  }
  return planInjuryConflictFor(exerciseName, injuryFlags);
}

export function isEquipmentAvailable(entry: CatalogueExercise, equipment: ReadonlyArray<Equipment>): boolean {
  if (entry.equipment === 'bodyweight') return true;
  return equipment.includes(entry.equipment);
}

export interface ExercisePoolFilter {
  track: TrainingTrack;
  equipment: ReadonlyArray<Equipment>;
  injuryFlags: ReadonlyArray<string>;
}

/**
 * Every catalogue entry that is legal for this customer: right track, available
 * equipment, and no injury conflict. Deterministic order (catalogue order).
 */
export function availableExercises(filter: ExercisePoolFilter): CatalogueExercise[] {
  return EXERCISE_CATALOGUE.filter((entry) => {
    if (!entry.tracks.includes(filter.track)) return false;
    if (!isEquipmentAvailable(entry, filter.equipment)) return false;
    if (injuryBlockFor(entry.name, filter.injuryFlags)) return false;
    return true;
  });
}

/** Entries the injury flags remove, with the reason — used by the rationale. */
export function blockedExercises(filter: ExercisePoolFilter): Array<{ entry: CatalogueExercise; hit: PlanInjuryHit }> {
  const blocked: Array<{ entry: CatalogueExercise; hit: PlanInjuryHit }> = [];
  for (const entry of EXERCISE_CATALOGUE) {
    if (!entry.tracks.includes(filter.track)) continue;
    if (!isEquipmentAvailable(entry, filter.equipment)) continue;
    const hit = injuryBlockFor(entry.name, filter.injuryFlags);
    if (hit) blocked.push({ entry, hit });
  }
  return blocked;
}

export interface SelectionRequest extends ExercisePoolFilter {
  style: DayStyle;
  experience: Experience;
  muscles: ReadonlyArray<MuscleGroup>;
  patterns: ReadonlyArray<MovementPattern>;
  maxExercises: number;
  /**
   * Rotates the candidate pool so days with the same label do not repeat the same
   * movements. Deterministic (it is the day index, never the clock).
   */
  varietyOffset?: number;
}

export interface SelectedExercise {
  entry: CatalogueExercise;
  reason: string;
}

function stylePool(pool: ReadonlyArray<CatalogueExercise>, style: DayStyle): CatalogueExercise[] {
  if (style === 'mixed') {
    return pool.filter(
      (entry) => entry.styles.includes('resistance') || entry.styles.includes('conditioning'),
    );
  }
  return pool.filter((entry) => entry.styles.includes(style));
}

function patternRank(entry: CatalogueExercise, patterns: ReadonlyArray<MovementPattern>): number {
  const index = patterns.indexOf(entry.pattern);
  return index === -1 ? patterns.length + 1 : index;
}

/** Stable sort: pattern priority, then compounds. Rotation supplies the tie-break. */
function byPriority(patterns: ReadonlyArray<MovementPattern>) {
  return (a: CatalogueExercise, b: CatalogueExercise): number => {
    const rank = patternRank(a, patterns) - patternRank(b, patterns);
    if (rank !== 0) return rank;
    if (a.isCompound !== b.isCompound) return a.isCompound ? -1 : 1;
    return 0;
  };
}

function takeFirst(
  candidates: ReadonlyArray<CatalogueExercise>,
  chosen: Set<string>,
): CatalogueExercise | null {
  for (const candidate of candidates) {
    if (chosen.has(candidate.slug)) continue;
    return candidate;
  }
  return null;
}

function rotatePool(
  pool: ReadonlyArray<CatalogueExercise>,
  offset: number | undefined,
): CatalogueExercise[] {
  if (!offset || pool.length === 0) return [...pool];
  const shift = ((offset % pool.length) + pool.length) % pool.length;
  return [...pool.slice(shift), ...pool.slice(0, shift)];
}

/** The day's own pattern priorities first, then the rest, with no duplicates. */
function patternOrder(dayPatterns: ReadonlyArray<MovementPattern>, fallback: ReadonlyArray<MovementPattern>): MovementPattern[] {
  const out: MovementPattern[] = [];
  for (const pattern of [...dayPatterns, ...fallback]) {
    if (!out.includes(pattern)) out.push(pattern);
  }
  return out;
}

/**
 * Fill a non-resistance day by cycling the pattern order: one from each family,
 * then the next from each family, until the exercise cap is reached. This keeps the
 * first pass broad (one erg, one sled, one carry) and only then adds volume.
 */
function pushByPattern(
  candidates: ReadonlyArray<CatalogueExercise>,
  chosen: Set<string>,
  order: ReadonlyArray<MovementPattern>,
  maxExercises: number,
  offset: number | undefined,
  push: (entry: CatalogueExercise | null, muscle: MuscleGroup | null) => void,
): void {
  let added = true;
  while (chosen.size < maxExercises && added) {
    added = false;
    for (const pattern of order) {
      if (chosen.size >= maxExercises) break;
      const forPattern = rotatePool(
        candidates.filter((entry) => entry.pattern === pattern && !chosen.has(entry.slug)),
        offset,
      );
      const pick = forPattern[0] ?? null;
      if (pick) {
        push(pick, null);
        added = true;
      }
    }
  }
}

function exerciseReason(entry: CatalogueExercise, muscle: MuscleGroup | null, style: DayStyle): string {
  const kind = entry.isCompound ? 'Compound' : 'Accessory';
  if (style === 'conditioning') return `Conditioning ${entry.pattern} for ${entry.muscleGroup}.`;
  if (style === 'power') return `Power ${entry.pattern} for ${entry.muscleGroup}.`;
  if (style === 'mobility') return `Movement-quality ${entry.pattern} for ${entry.muscleGroup}.`;
  return `${kind} ${entry.pattern} for ${muscle ?? entry.muscleGroup}.`;
}

/**
 * Pick a deterministic day's exercises.
 *
 * Resistance / mixed: tier 1 takes the best compound for each requested muscle in
 * order, tier 2 fills remaining slots with accessories, compounds always come
 * first. Conditioning: broad movement patterns first. Power: plyometrics and throws
 * before the strength base. Mobility: holds and stability work, no loading.
 */
export function selectExercises(request: SelectionRequest): SelectedExercise[] {
  const pool = availableExercises(request);
  const candidates = stylePool(rotatePool(pool, request.varietyOffset), request.style);
  const chosen = new Set<string>();
  const selected: SelectedExercise[] = [];

  const push = (entry: CatalogueExercise | null, muscle: MuscleGroup | null): void => {
    if (!entry || selected.length >= request.maxExercises) return;
    chosen.add(entry.slug);
    selected.push({ entry, reason: exerciseReason(entry, muscle, request.style) });
  };

  if (request.style === 'resistance' || request.style === 'mixed') {
    const ordered = [...candidates].sort(byPriority(request.patterns));
    const covered = new Set<MuscleGroup>();
    // Tier 1: one compound per muscle, in the day's muscle order.
    for (const muscle of request.muscles) {
      if (selected.length >= request.maxExercises) break;
      const forMuscle = ordered.filter((entry) => entry.muscleGroup === muscle && entry.isCompound);
      const pick = takeFirst(forMuscle, chosen);
      if (pick) covered.add(muscle);
      push(pick, muscle);
    }
    // Tier 2: accessories. Muscles with no compound of their own come first, so the
    // small groups (biceps, calves, core) are not crowded out by a long compound list.
    const accessoryOrder = [...request.muscles].sort((a, b) => {
      const aCovered = covered.has(a) ? 1 : 0;
      const bCovered = covered.has(b) ? 1 : 0;
      return aCovered - bCovered;
    });
    for (const muscle of accessoryOrder) {
      if (selected.length >= request.maxExercises) break;
      const forMuscle = ordered.filter((entry) => entry.muscleGroup === muscle && !entry.isCompound);
      push(takeFirst(forMuscle, chosen), muscle);
    }
    return selected;
  }

  if (request.style === 'conditioning') {
    // One of each conditioning pattern family; the day's own patterns lead.
    const order = patternOrder(request.patterns, [
      'erg',
      'machine_cardio',
      'sled',
      'carry',
      'rope',
      'jump_rope',
      'throw',
      'core',
      'mobility',
    ]);
    pushByPattern(candidates, chosen, order, request.maxExercises, request.varietyOffset, push);
    return selected;
  }

  if (request.style === 'power') {
    const order = patternOrder(request.patterns, ['plyometric', 'throw', 'sled', 'erg', 'machine_cardio']);
    pushByPattern(candidates, chosen, order, request.maxExercises, request.varietyOffset, push);
    return selected;
  }

  // mobility
  const order = patternOrder(request.patterns, ['mobility', 'stability', 'machine_cardio', 'core']);
  pushByPattern(candidates, chosen, order, request.maxExercises, request.varietyOffset, push);
  return selected;
}

/** Catalogue entries that are bodyweight-only — used by the wellness tests. */
export function bodyweightExercises(): CatalogueExercise[] {
  return EXERCISE_CATALOGUE.filter((entry) => entry.equipment === 'bodyweight');
}
